import type { AnalyticsEventName } from "./events";
import { ANALYTICS_EVENTS } from "./events";
import type { AnalyticsAdapter, AnalyticsProperties } from "./types";
import { isNoticeProductOnly } from "./noticePrivacy";

type MetaTrackMethod = "track" | "trackCustom";

type MappedMetaEvent = {
  method: MetaTrackMethod;
  name: string;
  properties: AnalyticsProperties;
};

type MetaFbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
};

type MetaWindow = Window & {
  fbq?: MetaFbq;
  _fbq?: MetaFbq;
};

const META_PIXEL_SCRIPT_ID = "meta-pixel-script";
const META_PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

function sanitizeMetaProperties(properties: AnalyticsProperties) {
  const sanitized: AnalyticsProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

export function mapAnalyticsEventToMetaEvent(
  name: AnalyticsEventName,
  properties: AnalyticsProperties = {},
): MappedMetaEvent | null {
  switch (name) {
    case ANALYTICS_EVENTS.PAGE_VIEWED:
      return {
        method: "track",
        name: "PageView",
        properties: {},
      };
    case ANALYTICS_EVENTS.REGISTRATION_COMPLETED:
      return {
        method: "track",
        name: "CompleteRegistration",
        properties: {},
      };
    case ANALYTICS_EVENTS.BILLING_SETUP_VIEWED:
      return {
        method: "track",
        name: "ViewContent",
        properties: {},
      };
    case ANALYTICS_EVENTS.BILLING_SETUP_STARTED:
    case ANALYTICS_EVENTS.TRIAL_UPGRADE_STARTED:
      return {
        method: "track",
        name: "InitiateCheckout",
        properties: {},
      };
    case ANALYTICS_EVENTS.BILLING_SETUP_COMPLETED:
      return {
        method: "track",
        name: "AddPaymentInfo",
        properties: {},
      };
    case ANALYTICS_EVENTS.TRIAL_UPGRADE_COMPLETED:
      return {
        method: "track",
        name: "Purchase",
        properties: sanitizeMetaProperties({
          value: properties.value,
          currency: properties.currency,
        }),
      };
    case ANALYTICS_EVENTS.FIRST_ROUTINE_CREATED:
      return {
        method: "trackCustom",
        name: "First Routine Created",
        properties: {},
      };
    default:
      return null;
  }
}

function ensureMetaPixel(windowObject: MetaWindow, documentObject: Document) {
  if (windowObject.fbq) {
    return;
  }

  const fbqImpl = (...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }

    fbq.queue?.push(args);
  };
  const fbq = fbqImpl as MetaFbq;

  fbq.queue = [];
  fbq.push = (...args: unknown[]) => {
    fbq(...args);
  };
  fbq.loaded = true;
  fbq.version = "2.0";

  windowObject.fbq = fbq;
  if (!windowObject._fbq) {
    windowObject._fbq = fbq;
  }

  if (documentObject.getElementById(META_PIXEL_SCRIPT_ID)) {
    return;
  }

  const script = documentObject.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SCRIPT_SRC;

  const [firstScript] = documentObject.getElementsByTagName("script");
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  documentObject.head?.appendChild(script);
}

export function createMetaAdapter(params: {
  enabled: boolean;
  pixelId: string;
  windowObject?: MetaWindow;
  documentObject?: Document;
  productOnly?: () => boolean;
}): AnalyticsAdapter {
  let initialized = false;
  const productOnly = params.productOnly ?? isNoticeProductOnly;

  return {
    destination: "advertising",
    init() {
      if (
        initialized
        || productOnly()
        || !params.enabled
        || !params.pixelId
        || !params.windowObject
        || !params.documentObject
      ) {
        return;
      }

      ensureMetaPixel(params.windowObject, params.documentObject);
      params.windowObject.fbq?.("init", params.pixelId);
      initialized = true;
    },
    track(name, properties = {}) {
      if (productOnly() || !params.enabled || !params.windowObject?.fbq) {
        return;
      }

      const mapped = mapAnalyticsEventToMetaEvent(name, properties);
      if (!mapped) {
        return;
      }

      params.windowObject.fbq(mapped.method, mapped.name, mapped.properties);
    },
    identify() {
      // Meta Pixel is anonymous-only in this phase.
    },
    reset() {
      // Meta Pixel identity reset is intentionally skipped in this phase.
    },
  };
}
